import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import Button from "components/buttons/Button";
import SwitchBtn from "components/buttons/SwitchBtn";
import InnerInput from "components/inputs/InnerInput/InnerInput";
import { Modal } from "components/Modal/Modal";
import { useAppDispatch } from "store";
import {
  createRole,
  RoleItem,
  RolePayload,
  updateRole,
} from "store/roles/slice";
import {
  NotificationTypes,
  showNotification,
} from "store/notifications/slice";

type Props = {
  mode: "create" | "edit";
  role?: RoleItem;
  onClose: () => void;
  onSaved: (role: RoleItem, payload: RolePayload) => Promise<void>;
};

const getApiMessage = (error: any) => {
  const data = error?.data ?? error?.response?.data;
  return (
    data?.message ||
    data?.error?.message ||
    data?.errors?.[0]?.message ||
    error?.message
  );
};

const RoleModal: React.FC<Props> = ({ mode, role, onClose, onSaved }) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const initial = useMemo<RolePayload>(
    () => ({
      title: role?.title ?? "",
      description: role?.description ?? "",
      isActive: mode === "create" ? true : (role?.isActive ?? true),
    }),
    [mode, role],
  );
  const [form, setForm] = useState<RolePayload>(initial);
  const [titleError, setTitleError] = useState("");
  const [apiError, setApiError] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const normalized: RolePayload = {
    title: form.title.trim(),
    description: form.description.trim(),
    isActive: form.isActive,
  };
  const hasChanges =
    normalized.title !== initial.title.trim() ||
    normalized.description !== initial.description.trim() ||
    normalized.isActive !== initial.isActive;
  const isSaveDisabled =
    !normalized.title || isSaving || (mode === "edit" && !hasChanges);

  const handleSave = async () => {
    if (!normalized.title) {
      setTitleError("Title is required");
      return;
    }
    if (mode === "edit" && (!role || role.isSystem)) return;

    setTitleError("");
    setApiError("");
    setIsSaving(true);
    try {
      const saved =
        mode === "create"
          ? await dispatch(createRole(normalized)).unwrap()
          : await dispatch(
              updateRole({ id: role!.id, data: normalized }),
            ).unwrap();

      await onSaved(
        { ...role, ...saved, ...normalized, id: saved?.id ?? role?.id } as RoleItem,
        normalized,
      );
      dispatch(
        showNotification({
          title: t("success"),
          message: t(
            mode === "create" ? "entityCreateSuccess" : "entityUpdateSuccess",
          ),
          type: NotificationTypes.success,
        }),
      );
      onClose();
    } catch (error: any) {
      const status = error?.status ?? error?.response?.status;
      const message = getApiMessage(error);
      if (status === 409 || status === 422) {
        setApiError(message || "Role with this title already exists");
      } else if (status === 403) {
        setApiError(message || "You do not have permission to perform this action");
      } else {
        dispatch(
          showNotification({
            title: t("error"),
            message: message || t("unknownError"),
            type: NotificationTypes.error,
          }),
        );
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal onClose={isSaving ? undefined : onClose} size="sm" className="role-modal">
      <Modal.Header>{mode === "create" ? "Create Role" : "Edit Role"}</Modal.Header>
      <Modal.Content>
        <div className="role-modal__top">
          <div className="role-modal__title-field">
            <InnerInput
              id="role-title"
              type="text"
              label="Title"
              isRequired
              placeholder="Enter Title"
              value={form.title}
              hasError={Boolean(titleError)}
              disabled={isSaving}
              onChange={(title) => {
                setForm((current) => ({ ...current, title }));
                if (title.trim()) setTitleError("");
                setApiError("");
              }}
              onBlur={() => {
                if (!form.title.trim()) setTitleError("Title is required");
              }}
            />
          </div>
          <SwitchBtn
            activeTxt="Active"
            isActive={form.isActive}
            disabled={isSaving}
            handleClick={() =>
              !isSaving &&
              setForm((current) => ({
                ...current,
                isActive: !current.isActive,
              }))
            }
          />
        </div>
        <InnerInput
          maxCodePoints={2500}
          id="role-description"
          type="textarea"
          rows={7}
          label="Description"
          placeholder="Enter Description"
          value={form.description}
          disabled={isSaving}
          onChange={(description) => {
            setForm((current) => ({ ...current, description }));
            setApiError("");
          }}
        />
        {apiError && <div className="role-modal__api-error">{apiError}</div>}
      </Modal.Content>
      <Modal.Footer>
        <Button isSecondary txt="Cancel" disabled={isSaving} handleClick={onClose} />
        <Button
          isPrimary
          txt="Save"
          isLoad={isSaving}
          disabled={isSaveDisabled}
          handleClick={handleSave}
        />
      </Modal.Footer>
    </Modal>
  );
};

export default RoleModal;
